import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ColorField } from './ColorPicker';
import { NumberInput, SelectInput } from '../dialogs/DialogShell';
import {
  ArrowUp, ArrowUpRight, ArrowRight, ArrowDownRight, ArrowDown, ArrowDownLeft, ArrowLeft, ArrowUpLeft,
  FlipHorizontal2, ArrowLeftRight, AlignHorizontalDistributeCenter, RotateCw, Plus, Trash2, Save, X, SlidersHorizontal, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEditor } from '../../context/EditorContext';
import { SCHEME_PRESETS, normalizeHex, isValidHex, clamp } from '../../lib/colors';
import {
  normalizeGradient, gradientToCss, gradientBarCss, colorAt, stopCss, describeGradient,
  GRADIENT_TYPES, RADIAL_SIZES, MAX_STOPS, GRADIENT_PRESETS, schemeGradients, reverseGradient, mirrorGradient, distributeStops,
} from '../../lib/gradients';
import { loadGradientPresets, getGradientPresets, onGradientPresetsChanged, saveGradientPreset, deleteGradientPreset } from '../../lib/gradientPresets';
import { apiErrorMessage } from '../../lib/projectApi';

/* ---------------- local model: stops carry a stable id while they are being dragged around ---------------- */
let seq = 0;
const nid = () => `gs${++seq}`;
const withIds = (g) => { const n = normalizeGradient(g); return { ...n, stops: n.stops.map((s) => ({ ...s, id: nid() })) }; };
const strip = (g) => normalizeGradient({ ...g, stops: g.stops.map(({ id, ...s }) => s) });
const sorted = (stops) => [...stops].sort((a, b) => a.position - b.position);

const DIRECTIONS = [
  { deg: 0, Icon: ArrowUp, name: 'Up' }, { deg: 45, Icon: ArrowUpRight, name: 'Up-right' }, { deg: 90, Icon: ArrowRight, name: 'Right' }, { deg: 135, Icon: ArrowDownRight, name: 'Down-right' },
  { deg: 180, Icon: ArrowDown, name: 'Down' }, { deg: 225, Icon: ArrowDownLeft, name: 'Down-left' }, { deg: 270, Icon: ArrowLeft, name: 'Left' }, { deg: 315, Icon: ArrowUpLeft, name: 'Up-left' },
];
const CENTER_PRESETS = [[0, 0], [50, 0], [100, 0], [0, 50], [50, 50], [100, 50], [0, 100], [50, 100], [100, 100]];

const hover = 'hover:bg-[hsl(var(--wp-command-hover))]';
const iconBtn = `h-7 w-7 inline-flex items-center justify-center rounded-md border border-border text-foreground ${hover} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none transition-colors duration-150`;
const seg = (active) => `h-7 px-2.5 text-xs rounded-md transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'bg-primary text-primary-foreground' : `text-muted-foreground ${hover} hover:text-foreground`}`;
const Label = ({ children, className = '' }) => <div className={`text-[11px] font-medium text-muted-foreground ${className}`}>{children}</div>;

/** Hue-style round angle control: drag the knob (Shift snaps to 15°). */
const AngleDial = ({ value, onChange, size = 44, testId }) => {
  const ref = useRef(null);
  const set = (e) => {
    const r = ref.current.getBoundingClientRect();
    const a = (Math.atan2(e.clientX - (r.left + r.width / 2), -(e.clientY - (r.top + r.height / 2))) * 180) / Math.PI;
    let deg = ((Math.round(a) % 360) + 360) % 360;
    if (e.shiftKey) deg = (Math.round(deg / 15) * 15) % 360;
    onChange(deg);
  };
  const rad = (value * Math.PI) / 180, c = size / 2, r = size / 2 - 5;
  return (
    <div
      ref={ref}
      role="slider" aria-label="Angle" aria-valuemin={0} aria-valuemax={360} aria-valuenow={Math.round(value)} tabIndex={0}
      className="relative shrink-0 rounded-full border border-border bg-background cursor-grab active:cursor-grabbing select-none touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ width: size, height: size }}
      onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); set(e); }}
      onPointerMove={(e) => { if (e.currentTarget.hasPointerCapture(e.pointerId)) set(e); }}
      onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onChange((value - (e.shiftKey ? 15 : 1) + 360) % 360); } if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onChange((value + (e.shiftKey ? 15 : 1)) % 360); } }}
      data-testid={testId}
    >
      <svg width={size} height={size} className="absolute inset-0" aria-hidden="true">
        <circle cx={c} cy={c} r={1.5} fill="hsl(var(--muted-foreground))" />
        <line x1={c} y1={c} x2={c + r * Math.sin(rad)} y2={c - r * Math.cos(rad)} stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" />
        <circle cx={c + r * Math.sin(rad)} cy={c - r * Math.cos(rad)} r={4} fill="hsl(var(--primary))" stroke="hsl(var(--background))" strokeWidth="1.5" />
      </svg>
    </div>
  );
};

/**
 * The gradient creator: stop bar with draggable stops, per-stop colour / position / opacity, linear angle (dial, slider,
 * direction presets, drag on the preview), radial centre / shape / extent, angular start angle, reverse / reflect /
 * distribute, and presets (curated, scheme-based, the user's own). `value` is a gradient object, `onChange` gets a
 * normalised gradient on every change (callers wrap it into `{ type: 'gradient', gradient }`).
 * `compact` is the Properties-pane layout (no presets / preview; `onMore` opens the full dialog).
 */
export const GradientEditor = ({ value, onChange, compact = false, onMore, testId = 'gradient-editor' }) => {
  const { state } = useEditor();
  const [g, setG] = useState(() => withIds(value));
  const [selId, setSelId] = useState(() => g.stops[0]?.id);
  const lastEmitted = useRef(JSON.stringify(strip(g)));
  const [presetTab, setPresetTab] = useState('presets');
  const [saveName, setSaveName] = useState('');
  const [, bump] = useState(0);
  useEffect(() => { if (!compact) { loadGradientPresets(); return onGradientPresetsChanged(() => bump((n) => n + 1)); } return undefined; }, [compact]);

  // external changes (Undo, a preset chosen elsewhere, another object selected) → resync the local model
  useEffect(() => {
    const inc = JSON.stringify(normalizeGradient(value));
    if (inc !== lastEmitted.current) { const next = withIds(value); lastEmitted.current = inc; setG(next); setSelId(next.stops[0]?.id); }
  }, [value]);

  const commit = useCallback((next) => {
    setG(next);
    const out = strip(next);
    lastEmitted.current = JSON.stringify(out);
    onChange(out);
  }, [onChange]);

  const stops = useMemo(() => sorted(g.stops), [g.stops]);
  const sel = g.stops.find((s) => s.id === selId) || stops[0];
  const selIndex = stops.findIndex((s) => s.id === sel?.id);
  const patch = (p) => commit({ ...g, ...p });
  const patchStop = (id, p) => commit({ ...g, stops: g.stops.map((s) => (s.id === id ? { ...s, ...p } : s)) });
  const css = gradientToCss(strip(g));

  /* ---------------- stops ---------------- */
  const addStopAt = (position) => {
    if (g.stops.length >= MAX_STOPS) { toast.info(`A gradient can have up to ${MAX_STOPS} stops.`); return; }
    const p = clamp(Math.round(position), 0, 100);
    const stop = { ...colorAt(strip(g), p), position: p, id: nid() };
    commit({ ...g, stops: [...g.stops, stop] });
    setSelId(stop.id);
  };
  const addStopInLargestGap = () => {
    let best = 0, at = 50;
    for (let i = 0; i + 1 < stops.length; i++) { const gap = stops[i + 1].position - stops[i].position; if (gap > best) { best = gap; at = (stops[i].position + stops[i + 1].position) / 2; } }
    if (stops[0].position > best) { best = stops[0].position; at = stops[0].position / 2; }
    if (100 - stops[stops.length - 1].position > best) at = (100 + stops[stops.length - 1].position) / 2;
    addStopAt(at);
  };
  const removeStop = (id) => {
    if (g.stops.length <= 2) return;
    const i = stops.findIndex((s) => s.id === id);
    const next = g.stops.filter((s) => s.id !== id);
    commit({ ...g, stops: next });
    const remaining = sorted(next);
    setSelId((remaining[Math.min(i, remaining.length - 1)] || remaining[0]).id);
  };

  const barRef = useRef(null);
  const drag = useRef(null); // { id, removing }
  const posFromEvent = (e) => { const r = barRef.current.getBoundingClientRect(); return clamp(((e.clientX - r.left) / r.width) * 100, 0, 100); };
  const onBarPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const p = posFromEvent(e);
    addStopAt(p);
  };
  const onHandlePointerDown = (e, id) => {
    if (e.button !== 0) return;
    e.stopPropagation(); e.preventDefault();
    setSelId(id);
    drag.current = { id, removing: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onHandlePointerMove = (e) => {
    if (!drag.current || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const r = barRef.current.getBoundingClientRect();
    const removing = g.stops.length > 2 && e.clientY > r.bottom + 44;
    drag.current.removing = removing;
    const p = Math.round(posFromEvent(e) * 2) / 2;
    const cur = g.stops.find((s) => s.id === drag.current.id);
    if (cur && (cur.position !== p || cur.removing !== removing)) commit({ ...g, stops: g.stops.map((s) => (s.id === drag.current.id ? { ...s, position: p, removing } : s)) });
  };
  const onHandlePointerUp = (e) => {
    if (!drag.current) return;
    const { id, removing } = drag.current;
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    if (removing) removeStop(id);
    else if (g.stops.some((s) => s.id === id && s.removing)) commit({ ...g, stops: g.stops.map((s) => (s.id === id ? { ...s, removing: false } : s)) });
  };
  const onHandleKeyDown = (e, s) => {
    const step = e.shiftKey ? 10 : 1;
    if (e.key === 'ArrowLeft') { e.preventDefault(); patchStop(s.id, { position: clamp(s.position - step, 0, 100) }); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); patchStop(s.id, { position: clamp(s.position + step, 0, 100) }); }
    else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeStop(s.id); }
  };

  /* ---------------- preview drag (angle for linear / angular, centre for radial) ---------------- */
  const previewRef = useRef(null);
  const previewDrag = (e) => {
    const r = previewRef.current.getBoundingClientRect();
    if (g.type === 'radial' || (g.type === 'conic' && e.altKey)) {
      patch({ center: { x: Math.round(clamp(((e.clientX - r.left) / r.width) * 100, 0, 100)), y: Math.round(clamp(((e.clientY - r.top) / r.height) * 100, 0, 100)) } });
    } else {
      const cx = r.left + (g.type === 'conic' ? (g.center.x / 100) * r.width : r.width / 2), cy = r.top + (g.type === 'conic' ? (g.center.y / 100) * r.height : r.height / 2);
      let deg = ((Math.round((Math.atan2(e.clientX - cx, -(e.clientY - cy)) * 180) / Math.PI) % 360) + 360) % 360;
      if (e.shiftKey) deg = (Math.round(deg / 15) * 15) % 360;
      patch({ angle: deg });
    }
  };

  /* ---------------- hex field for the selected stop ---------------- */
  const [hex, setHex] = useState(sel?.color || '');
  const hasSel = !!sel;
  const selColor = sel?.color;
  const selStopId = sel?.id;
  // follows the selected stop's colour / identity only (not its position while it is dragged)
  useEffect(() => { setHex((h) => (hasSel && isValidHex(h) && normalizeHex(h) === selColor ? h : selColor || '')); }, [hasSel, selColor, selStopId]);

  /* ---------------- presets ---------------- */
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const schemeList = useMemo(() => schemeGradients(scheme), [scheme]);
  const mine = getGradientPresets();
  const currentJson = JSON.stringify(strip(g));
  const applyPreset = (gradient) => { const next = withIds(gradient); commit(next); setSelId(next.stops[0].id); };
  const savePreset = async () => {
    try {
      const p = await saveGradientPreset(saveName || describeGradient(strip(g)), strip(g));
      toast.success(`Gradient “${p.name}” saved to My gradients`, { id: 'gradient-save' });
      setSaveName('');
      setPresetTab('mine');
    } catch (e) { toast.error(await apiErrorMessage(e, 'The gradient could not be saved.')); }
  };
  const tile = (p, extra) => {
    const active = JSON.stringify(normalizeGradient(p.gradient)) === currentJson;
    return (
      <div key={p.id} className="relative group">
        <button type="button" title={p.name} aria-label={p.name} onClick={() => applyPreset(p.gradient)} className={`h-9 w-full rounded-md border wp-checker overflow-hidden transition-[transform,box-shadow] duration-150 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`} data-testid={`${testId}-preset-${p.id}`} data-state={active ? 'selected' : 'idle'}>
          <span className="block h-full w-full" style={{ background: gradientToCss(p.gradient) }} />
        </button>
        {extra}
      </div>
    );
  };

  const typeBar = (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-border p-0.5 bg-background" role="tablist" aria-label="Gradient type" data-testid={`${testId}-types`}>
      {GRADIENT_TYPES.map((t) => <button key={t.id} type="button" role="tab" aria-selected={g.type === t.id} onClick={() => patch({ type: t.id })} className={seg(g.type === t.id)} data-testid={`${testId}-type-${t.id}`}>{t.label}</button>)}
    </div>
  );

  const actions = (
    <div className="flex items-center gap-1" data-testid={`${testId}-actions`}>
      <button type="button" className={iconBtn} title="Reverse the colours" onClick={() => applyPreset(reverseGradient(strip(g)))} data-testid={`${testId}-reverse`}><ArrowLeftRight className="h-3.5 w-3.5" /></button>
      <button type="button" className={iconBtn} title="Reflect: mirror the colours around the middle" onClick={() => applyPreset(mirrorGradient(strip(g)))} disabled={g.stops.length * 2 - 1 > MAX_STOPS} data-testid={`${testId}-mirror`}><FlipHorizontal2 className="h-3.5 w-3.5" /></button>
      <button type="button" className={iconBtn} title="Distribute the stops evenly" onClick={() => applyPreset(distributeStops(strip(g)))} data-testid={`${testId}-distribute`}><AlignHorizontalDistributeCenter className="h-3.5 w-3.5" /></button>
      {g.type !== 'radial' && <button type="button" className={iconBtn} title="Rotate 90°" onClick={() => patch({ angle: (g.angle + 90) % 360 })} data-testid={`${testId}-rotate`}><RotateCw className="h-3.5 w-3.5" /></button>}
    </div>
  );

  // The end handles overhang the bar by half their width (8 px); in the narrow Properties pane the wrapper keeps that
  // inside the pane (the pane clips horizontally), in the dialog the dialog padding already has room for it.
  const bar = (
    <div className={`pt-2 pb-6 ${compact ? 'px-2' : ''}`} data-testid={`${testId}-bar-wrap`}>
      <div
        ref={barRef}
        className="relative h-7 rounded-md border border-border wp-checker cursor-copy select-none touch-none"
        onPointerDown={onBarPointerDown}
        title="Click to add a colour stop"
        data-testid={`${testId}-bar`}
        data-stops={g.stops.length}
      >
        <div className="absolute inset-0 rounded-[5px]" style={{ background: gradientBarCss(strip(g)) }} />
        {stops.map((s, i) => {
          const active = s.id === sel?.id;
          return (
            <button
              key={s.id}
              type="button"
              className={`absolute -bottom-5 h-6 w-4 -translate-x-1/2 rounded-b-md rounded-t-sm border-2 wp-checker shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-ew-resize touch-none transition-[opacity,transform] duration-150 ${active ? 'border-primary z-10 scale-110' : 'border-background dark:border-foreground/70'} ${s.removing ? 'opacity-40' : ''}`}
              style={{ left: `${s.position}%` }}
              onPointerDown={(e) => onHandlePointerDown(e, s.id)}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              onKeyDown={(e) => onHandleKeyDown(e, s)}
              onClick={(e) => e.stopPropagation()}
              title={`${s.color.toUpperCase()} at ${Math.round(s.position)}%${s.opacity < 100 ? ` · ${Math.round(s.opacity)}% opacity` : ''} — drag to move, drag down to remove`}
              aria-label={`Colour stop ${i + 1}: ${s.color} at ${Math.round(s.position)} percent`}
              aria-pressed={active}
              data-testid={`${testId}-stop-${i}`}
              data-position={Math.round(s.position)}
              data-color={s.color}
              data-selected={active ? '1' : undefined}
            >
              <span className="absolute inset-0 rounded-[3px]" style={{ background: stopCss(s) }} />
              <span className="absolute -top-[7px] left-1/2 -translate-x-1/2 border-x-[5px] border-b-[6px] border-x-transparent" style={{ borderBottomColor: active ? 'hsl(var(--primary))' : s.color }} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );

  const stopControls = sel && (
    <div className="space-y-2" data-testid={`${testId}-stop-controls`}>
      <div className="flex items-center gap-2">
        <Label className="shrink-0">Stop {selIndex + 1} of {stops.length}</Label>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" className={iconBtn} title="Add a stop in the largest gap" onClick={addStopInLargestGap} disabled={g.stops.length >= MAX_STOPS} data-testid={`${testId}-stop-add`}><Plus className="h-3.5 w-3.5" /></button>
          <button type="button" className={`${iconBtn} hover:text-destructive`} title="Remove this stop" onClick={() => removeStop(sel.id)} disabled={g.stops.length <= 2} data-testid={`${testId}-stop-delete`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className={`grid gap-2 ${compact ? 'grid-cols-[1fr_88px]' : 'grid-cols-[1fr_110px]'}`}>
        <ColorField value={sel.color} onChange={(v) => v && patchStop(sel.id, { color: normalizeHex(v) })} allowNoFill={false} label="Stop" testId={`${testId}-stop-color`} />
        <Input value={hex} onChange={(e) => { setHex(e.target.value); if (isValidHex(e.target.value)) patchStop(sel.id, { color: normalizeHex(e.target.value) }); }} onBlur={() => setHex(sel.color)} className="h-8 font-mono text-xs uppercase" aria-label="Stop colour (hex)" spellCheck={false} data-testid={`${testId}-stop-hex`} />
      </div>
      <div className="grid grid-cols-[52px_1fr_64px] items-center gap-2">
        <Label>Position</Label>
        <Slider value={[sel.position]} min={0} max={100} step={0.5} onValueChange={([v]) => patchStop(sel.id, { position: v })} aria-label="Stop position" data-testid={`${testId}-stop-position-slider`} />
        <NumberInput value={Math.round(sel.position * 10) / 10} onChange={(v) => patchStop(sel.id, { position: clamp(v, 0, 100) })} min={0} max={100} step={1} suffix="%" testId={`${testId}-stop-position`} />
      </div>
      <div className="grid grid-cols-[52px_1fr_64px] items-center gap-2">
        <Label>Opacity</Label>
        <Slider value={[sel.opacity]} min={0} max={100} step={1} onValueChange={([v]) => patchStop(sel.id, { opacity: v })} aria-label="Stop opacity" data-testid={`${testId}-stop-opacity-slider`} />
        <NumberInput value={Math.round(sel.opacity)} onChange={(v) => patchStop(sel.id, { opacity: clamp(v, 0, 100) })} min={0} max={100} step={5} suffix="%" testId={`${testId}-stop-opacity`} />
      </div>
    </div>
  );

  const angleControls = (
    <div className="space-y-2" data-testid={`${testId}-angle-controls`}>
      <div className="flex items-center gap-2">
        {!compact && <AngleDial value={g.angle} onChange={(deg) => patch({ angle: deg })} testId={`${testId}-angle-dial`} />}
        <div className="flex-1 grid grid-cols-[52px_1fr_64px] items-center gap-2">
          <Label>{g.type === 'conic' ? 'Start' : 'Angle'}</Label>
          <Slider value={[g.angle]} min={0} max={360} step={1} onValueChange={([v]) => patch({ angle: v % 360 })} aria-label="Angle" data-testid={`${testId}-angle-slider`} />
          <NumberInput value={Math.round(g.angle)} onChange={(v) => patch({ angle: ((v % 360) + 360) % 360 })} min={-360} max={720} step={5} suffix="°" testId={`${testId}-angle`} />
        </div>
      </div>
      {g.type === 'linear' && (
        <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Direction" data-testid={`${testId}-directions`}>
          {DIRECTIONS.map(({ deg, Icon, name }) => <button key={deg} type="button" className={`${iconBtn} ${g.angle === deg ? 'border-primary bg-primary/10 text-primary' : ''}`} title={`${name} (${deg}°)`} aria-pressed={g.angle === deg} onClick={() => patch({ angle: deg })} data-testid={`${testId}-dir-${deg}`}><Icon className="h-3.5 w-3.5" /></button>)}
        </div>
      )}
    </div>
  );

  const centerControls = (
    <div className="space-y-2" data-testid={`${testId}-center-controls`}>
      <div className="flex gap-2">
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-[52px_1fr_64px] items-center gap-2">
            <Label>Centre X</Label>
            <Slider value={[g.center.x]} min={0} max={100} step={1} onValueChange={([v]) => patch({ center: { ...g.center, x: v } })} aria-label="Centre X" data-testid={`${testId}-center-x-slider`} />
            <NumberInput value={Math.round(g.center.x)} onChange={(v) => patch({ center: { ...g.center, x: clamp(v, 0, 100) } })} min={0} max={100} step={5} suffix="%" testId={`${testId}-center-x`} />
          </div>
          <div className="grid grid-cols-[52px_1fr_64px] items-center gap-2">
            <Label>Centre Y</Label>
            <Slider value={[g.center.y]} min={0} max={100} step={1} onValueChange={([v]) => patch({ center: { ...g.center, y: v } })} aria-label="Centre Y" data-testid={`${testId}-center-y-slider`} />
            <NumberInput value={Math.round(g.center.y)} onChange={(v) => patch({ center: { ...g.center, y: clamp(v, 0, 100) } })} min={0} max={100} step={5} suffix="%" testId={`${testId}-center-y`} />
          </div>
        </div>
        {!compact && (
          <div className="grid grid-cols-3 gap-0.5 shrink-0 self-center" role="group" aria-label="Centre presets" data-testid={`${testId}-center-presets`}>
            {CENTER_PRESETS.map(([x, y]) => <button key={`${x}-${y}`} type="button" onClick={() => patch({ center: { x, y } })} className={`h-5 w-5 rounded-sm border ${g.center.x === x && g.center.y === y ? 'border-primary bg-primary/20' : `border-border ${hover}`}`} title={`Centre at ${x}%, ${y}%`} aria-label={`Centre at ${x}%, ${y}%`} data-testid={`${testId}-center-${x}-${y}`}><span className="block h-1.5 w-1.5 rounded-full bg-current mx-auto opacity-60" /></button>)}
          </div>
        )}
      </div>
      {g.type === 'radial' && (
        <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-[auto_1fr]'} items-center`}>
          <div className="inline-flex items-center gap-0.5 rounded-md border border-border p-0.5 bg-background" role="tablist" aria-label="Shape">
            {[['circle', 'Circle'], ['ellipse', 'Ellipse']].map(([id, l]) => <button key={id} type="button" role="tab" aria-selected={g.shape === id} onClick={() => patch({ shape: id })} className={seg(g.shape === id)} data-testid={`${testId}-shape-${id}`}>{l}</button>)}
          </div>
          <SelectInput value={g.size} onChange={(v) => patch({ size: v })} options={RADIAL_SIZES.map((s) => ({ value: s.id, label: s.label }))} className="w-full" testId={`${testId}-size`} />
        </div>
      )}
    </div>
  );

  /* ---------------- compact (Properties pane) ---------------- */
  if (compact) {
    return (
      <div className="space-y-3 min-w-0" data-testid={testId}>
        <div className="flex items-center justify-between gap-1.5 flex-wrap">{typeBar}{actions}</div>
        {bar}
        {stopControls}
        {g.type === 'radial' ? centerControls : angleControls}
        {g.type === 'conic' && centerControls}
        {onMore && <Button type="button" variant="outline" size="sm" className="w-full h-8 text-xs" onClick={onMore} data-testid={`${testId}-more`}><SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" /> More gradient options…</Button>}
      </div>
    );
  }

  /* ---------------- full (docked dialog — one column, the publication stays visible beside it) ---------------- */
  const previewHandle = g.type === 'linear'
    ? (() => { const rad = (g.angle * Math.PI) / 180; return { x: 50 + 42 * Math.sin(rad), y: 50 - 42 * Math.cos(rad) }; })()
    : { x: g.center.x, y: g.center.y };
  const presetGrid = 'grid grid-cols-6 gap-1.5';
  return (
    <div className="space-y-3 min-w-0" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">{typeBar}{actions}</div>
      <div
        ref={previewRef}
        className="relative h-28 rounded-md border border-border wp-checker overflow-hidden select-none touch-none cursor-crosshair"
        onPointerDown={(e) => { if (e.button !== 0) return; e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); previewDrag(e); }}
        onPointerMove={(e) => { if (e.currentTarget.hasPointerCapture(e.pointerId)) previewDrag(e); }}
        title={g.type === 'radial' ? 'Drag to move the centre' : g.type === 'conic' ? 'Drag to turn the start angle · Alt-drag to move the centre' : 'Drag to set the angle (Shift snaps to 15°)'}
        data-testid={`${testId}-preview`}
      >
        <div className="absolute inset-0" style={{ background: css }} />
        <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {g.type === 'linear' && <line x1={100 - previewHandle.x} y1={100 - previewHandle.y} x2={previewHandle.x} y2={previewHandle.y} stroke="rgba(255,255,255,0.85)" strokeWidth="0.6" strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />}
          {g.type === 'conic' && <line x1={g.center.x} y1={g.center.y} x2={g.center.x + 40 * Math.sin((g.angle * Math.PI) / 180)} y2={g.center.y - 40 * Math.cos((g.angle * Math.PI) / 180)} stroke="rgba(255,255,255,0.85)" strokeWidth="0.6" strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />}
        </svg>
        <span className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow-md" style={{ left: `${previewHandle.x}%`, top: `${previewHandle.y}%` }} aria-hidden="true" data-testid={`${testId}-preview-handle`} />
        <span className="absolute bottom-1 right-1.5 rounded-sm bg-black/45 px-1.5 py-0.5 text-[10px] text-white" data-testid={`${testId}-summary`}>{describeGradient(strip(g))}</span>
      </div>
      {bar}
      {stopControls}
      <div className="space-y-3 pt-2 border-t border-border">
        {g.type !== 'radial' && angleControls}
        {g.type !== 'linear' && centerControls}
      </div>

      <div className="space-y-2 pt-2 border-t border-border min-w-0" data-testid={`${testId}-presets`}>
        <div className="flex items-center gap-2">
          <Label>Presets</Label>
          <div className="ml-auto inline-flex items-center gap-0.5 rounded-md border border-border p-0.5 bg-background" role="tablist" aria-label="Preset groups">
            {[['presets', 'Gallery'], ['scheme', 'Scheme'], ['mine', 'Mine']].map(([id, l]) => <button key={id} type="button" role="tab" aria-selected={presetTab === id} onClick={() => setPresetTab(id)} className={`${seg(presetTab === id)} px-2`} data-testid={`${testId}-presets-tab-${id}`}>{id === 'mine' ? <span className="inline-flex items-center gap-1"><Star className="h-3 w-3" /> {l}{mine.length ? ` (${mine.length})` : ''}</span> : l}</button>)}
          </div>
        </div>
        <div className="max-h-[140px] overflow-y-auto pr-0.5 pt-1">
          {presetTab === 'presets' && <div className={presetGrid}>{GRADIENT_PRESETS.map((p) => tile(p))}</div>}
          {presetTab === 'scheme' && (
            <div className="space-y-1.5">
              <div className={presetGrid}>{schemeList.map((p) => tile(p))}</div>
              <p className="text-[11px] text-muted-foreground">Built from the publication's colour scheme.</p>
            </div>
          )}
          {presetTab === 'mine' && (
            <div className="space-y-2">
              {mine.length === 0 && <p className="text-[11px] text-muted-foreground py-1" data-testid={`${testId}-mine-empty`}>No saved gradients yet. Name the current one below and save it — it is then offered for every fill in every publication.</p>}
              {mine.length > 0 && <div className={presetGrid}>{mine.map((p) => tile(p, <button type="button" onClick={async () => { try { await deleteGradientPreset(p.id); toast.success(`“${p.name}” removed`, { id: 'gradient-save' }); } catch (e) { toast.error(await apiErrorMessage(e, 'Could not remove the gradient.')); } }} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-background border border-border text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 focus:opacity-100 flex items-center justify-center" title={`Remove “${p.name}”`} aria-label={`Remove ${p.name}`} data-testid={`${testId}-preset-delete-${p.id}`}><X className="h-2.5 w-2.5" /></button>))}</div>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder={`Save as… (${describeGradient(strip(g))})`} className="h-8 text-xs" aria-label="Name for the saved gradient" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); savePreset(); } }} data-testid={`${testId}-save-name`} />
          <Button type="button" size="sm" variant="outline" className="h-8 px-2.5 shrink-0 text-xs" onClick={savePreset} title="Save the current gradient to My gradients" data-testid={`${testId}-save`}><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
        </div>
      </div>
    </div>
  );
};
